import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TAG_COLORS = [
  '#667eea', '#e53e3e', '#38a169', '#dd6b20',
  '#d69e2e', '#805ad5', '#319795', '#2b6cb0'
];

const TagSelector = ({ patientId, selectedTags, onChange, headers }) => {
  const [patientTags, setPatientTags] = useState([]);
  const [predefined, setPredefined] = useState({});
  const [showDropdown, setShowDropdown] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#667eea');
  const [activeCategory, setActiveCategory] = useState('specialty');

  useEffect(() => {
    fetchTags();
    fetchPredefined();
  }, [patientId]);

  const fetchTags = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/tags/patient/${patientId}`, { headers });
      setPatientTags(res.data.tags || []);
    } catch (error) {
      console.error('Error fetching patient tags:', error);
    }
  };

  const fetchPredefined = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/tags/predefined`, { headers });
      setPredefined(res.data.predefined || {});
    } catch (error) {
      console.error('Error fetching predefined tags:', error);
    }
  };

  const createAndSelectTag = async (name, category, color) => {
    try {
      const res = await axios.post(
        `${API_URL}/tags/patient/${patientId}`,
        { name, category, color },
        { headers }
      );
      const newTag = res.data.tag;
      await fetchTags();
      if (!selectedTags.find(t => t.id === newTag.id)) {
        onChange([...selectedTags, newTag]);
      }
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  const toggleTag = (tag) => {
    const isSelected = selectedTags.find(t => t.id === tag.id);
    if (isSelected) {
      onChange(selectedTags.filter(t => t.id !== tag.id));
    } else {
      onChange([...selectedTags, tag]);
    }
  };

  const selectPredefined = async (item, category) => {
    await createAndSelectTag(item.name, category, item.color);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Selected tags display */}
      <div
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          minHeight: '42px',
          padding: '6px 12px',
          border: '2px solid #e2e8f0',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
          background: 'white'
        }}
      >
        {selectedTags.length === 0 ? (
          <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
            🏷️ Add tags (e.g. Gastroenterology, Annual Checkup)
          </span>
        ) : (
          selectedTags.map(tag => (
            <span key={tag.id} style={{
              background: tag.color + '20',
              color: tag.color,
              border: `1px solid ${tag.color}40`,
              borderRadius: '12px',
              padding: '2px 10px',
              fontSize: '0.8rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {tag.name}
              <span
                onClick={(e) => { e.stopPropagation(); toggleTag(tag); }}
                style={{ cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem' }}
              >×</span>
            </span>
          ))
        )}
        <span style={{ marginLeft: 'auto', color: '#a0aec0', fontSize: '0.8rem' }}>
          {showDropdown ? '▲' : '▼'}
        </span>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '2px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 1000,
          maxHeight: '380px',
          overflow: 'auto',
          marginTop: '4px'
        }}>
          {/* Category tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            padding: '8px 8px 0'
          }}>
            {['specialty', 'visit_type', 'custom'].map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeCategory === cat ? '600' : '400',
                  color: activeCategory === cat ? '#667eea' : '#718096',
                  borderBottom: activeCategory === cat ? '2px solid #667eea' : '2px solid transparent',
                  marginBottom: '-1px'
                }}>
                {cat === 'specialty' ? '🏥 Specialty' :
                 cat === 'visit_type' ? '📅 Visit Type' : '🏷️ Custom'}
              </button>
            ))}
          </div>

          <div style={{ padding: '12px' }}>
            {/* Predefined tags */}
            {activeCategory !== 'custom' && predefined[activeCategory] && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {predefined[activeCategory].map(item => {
                  const isSelected = selectedTags.find(t => t.name === item.name);
                  return (
                    <button key={item.name}
                      onClick={() => selectPredefined(item, activeCategory)}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        border: `1px solid ${item.color}`,
                        background: isSelected ? item.color : item.color + '15',
                        color: isSelected ? 'white' : item.color,
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                      {isSelected ? '✓ ' : ''}{item.name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Custom tags from patient */}
            {activeCategory === 'custom' && (
              <>
                {patientTags.filter(t => t.category === 'custom').length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {patientTags.filter(t => t.category === 'custom').map(tag => {
                      const isSelected = selectedTags.find(t2 => t2.id === tag.id);
                      return (
                        <button key={tag.id} onClick={() => toggleTag(tag)}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            border: `1px solid ${tag.color}`,
                            background: isSelected ? tag.color : tag.color + '15',
                            color: isSelected ? 'white' : tag.color,
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                          {isSelected ? '✓ ' : ''}{tag.name}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Create new custom tag */}
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '8px' }}>
                    Create new tag:
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={newTagName}
                      onChange={e => setNewTagName(e.target.value)}
                      placeholder="Tag name (e.g. H.Pylori Treatment)"
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem' }}
                      onKeyPress={e => {
                        if (e.key === 'Enter' && newTagName.trim()) {
                          createAndSelectTag(newTagName.trim(), 'custom', newTagColor);
                          setNewTagName('');
                        }
                      }}
                    />
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {TAG_COLORS.map(c => (
                        <div key={c} onClick={() => setNewTagColor(c)}
                          style={{
                            width: '18px', height: '18px',
                            borderRadius: '50%',
                            background: c,
                            cursor: 'pointer',
                            border: newTagColor === c ? '2px solid #2d3748' : '2px solid transparent'
                          }} />
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        if (newTagName.trim()) {
                          createAndSelectTag(newTagName.trim(), 'custom', newTagColor);
                          setNewTagName('');
                        }
                      }}
                      style={{ padding: '6px 12px', background: '#667eea', color: 'white',
                        border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                      Add
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSelector;
